import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  deleteAttachments,
  emailExists,
  getCities,
  getClientTypesList,
  getDegreesList,
  getCertificatesList,
  getLicensesList,
  getStatesList,
  getTratmentProgramConstants,
  sendFeedback,
  uploadFile,
  uploadImage,
  getZipcodesList,
  verifyUser,
  unsubscribeEmail,
  unsubscribeAllEmails,
  getReleaseVersion,
} from "../services/common.services";
import {
  ERROR,
  LOADING,
  SUCCESS,
  detox,
  facility_types,
  payment_options,
  services,
  sortData,
  treatment,
} from "../constants/common";
import { ReactComponent as WarningIcon } from "../assets/Icons/warning.svg";
import { useAlert } from "./Alert";

const CommonContext = createContext({});

export const CommonContextProvider = ({ children }) => {
  const { sendAlert } = useAlert();

  const [common, setCommon] = useState({
    statesList: [],
    fetchStatesStatus: "",
    degreesList: [],
    fetchDegreesStatus: "",
    licensesList: [],
    fetchLicensesStatus: "",
    certificatesList: [],
    fetchCertificatesStatus: "",
    clientTypesList: [],
    releaseVersion: [],
    fetchClientTypesStatus: "",
    emailExists: false,
    emailExistsStatus: "",
    uploadFilesStatus: "",
    deleteAttachmentStatus: "",
    uploadImageStatus: "",
    fetchTreatmentTypesStatus: "",
    fetchServicesStatus: "",
    fetchDetoxListStatus: "",
    fetchPaymentOptionsStatus: "",
    fetchFacilityTypesStatus: "",
    treatmentTypesList: [],
    servicesList: [],
    detoxList: [],
    paymentOptionsList: [],
    facilityTypesList: [],
    citiesList: [],
    fetchCitiesStatus: "",
    zipcodesList: [],
    fetchZipcodesStatus: "",
  });

  function handleState(obj) {
    setCommon((prevState) => ({
      ...prevState,
      ...obj,
    }));
  }

  async function checkIfEmailExists(email) {
    handleState({ emailExistsStatus: LOADING });
    try {
      const response = await emailExists(email);
      handleState({
        emailExists: response.data.code,
        emailExistsStatus: SUCCESS,
      });

      return response;
    } catch (error) {
      handleState({ emailExistsStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function verifyEmailUser(data) {
    // handleState({ emailExistsStatus: LOADING });
    try {
      const response = await verifyUser(data);
      // handleState({
      //   emailExists: response.data.code,
      //   emailExistsStatus: SUCCESS,
      // });

      return response;
    } catch (error) {
      // handleState({ emailExistsStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function unsubscribeEmailNotification(data) {
    // handleState({ emailExistsStatus: LOADING });
    try {
      const response = await unsubscribeEmail(data);
      // handleState({
      //   emailExists: response.data.code,
      //   emailExistsStatus: SUCCESS,
      // });

      return response;
    } catch (error) {
      // handleState({ emailExistsStatus: ERROR });
      Promise.reject(error);
    }
  }

  async function unsubscribeAllEmailNotification(data) {
    // handleState({ emailExistsStatus: LOADING });
    try {
      const response = await unsubscribeAllEmails(data);
      // handleState({
      //   emailExists: response.data.code,
      //   emailExistsStatus: SUCCESS,
      // });

      return response;
    } catch (error) {
      // handleState({ emailExistsStatus: ERROR });
      Promise.reject(error);
    }
  }

  const getStatebyId = async (id) => {
    const statesList = await fetchStatesList();
    const foundState = statesList.find((state) => state?.id === id);
    return foundState;
  };

  async function fetchStatesList() {
    if (common.statesList.length === 0) {
      handleState({ fetchStatesStatus: LOADING });
      try {
        const response = await getStatesList();
        handleState({
          statesList: sortData(response.data.data),
          fetchStatesStatus: SUCCESS,
        });
        return response.data.data;
      } catch (error) {
        handleState({ fetchStatesStatus: ERROR });
        Promise.reject(error);
      }
    }
    return common.statesList;
  }

  const clearCitiesList = () => {
    handleState({ citiesList: [] });
  };

  const clearZipcodesList = () => {
    handleState({ zipcodesList: [] });
  };

  const getStateIdByAbbreviation = async (abbreviation) => {
    const list = common.statesList?.length
      ? common.statesList
      : await fetchStatesList();
    const state = list?.find((state) => state.abbreviation === abbreviation);
    return state ? state.id : null;
  };

  async function fetchDegreesList() {
    if (common.degreesList.length === 0) {
      handleState({ fetchDegreesStatus: LOADING });
      try {
        const response = await getDegreesList();
        handleState({
          degreesList: response.data.data,
          fetchDegreesStatus: SUCCESS,
        });
      } catch (error) {
        handleState({ fetchDegreesStatus: ERROR });
        Promise.reject(error);
      }
    }
  }

  async function fetchLicensesList() {
    if (common.licensesList.length === 0) {
      handleState({ fetchLicensesStatus: LOADING });
      try {
        const response = await getLicensesList();
        handleState({
          licensesList: response.data.data,
          fetchLicensesStatus: SUCCESS,
        });
      } catch (error) {
        handleState({ fetchLicensesStatus: ERROR });
        Promise.reject(error);
      }
    }
  }

  async function fetchCertificatesList() {
    if (common.certificatesList.length === 0) {
      const response = await getCertificatesList();
      handleState({ fetchCertificatesStatus: LOADING });
      try {
        handleState({
          certificatesList: response.data.data,
          fetchCertificatesStatus: SUCCESS,
        });
      } catch (error) {
        handleState({ fetchCertificatesStatus: ERROR });
        Promise.reject(error);
      }
    }
  }

  async function fetchClientTypesList() {
    if (common.clientTypesList.length === 0) {
      handleState({ fetchClientTypesStatus: LOADING });
      try {
        const response = await getClientTypesList();
        handleState({
          clientTypesList: response.data.data,
          fetchClientTypesStatus: SUCCESS,
        });
      } catch (error) {
        handleState({ fetchClientTypesStatus: ERROR });
        Promise.reject(error);
      }
    }
  }

  async function fetchReleaseVersion() {
    try {
      const response = await getReleaseVersion();
      handleState({
        releaseVersion: response?.data,
      });
    } catch (error) {
      Promise.reject(error);
    }
  }

  async function createFeedback(data) {
    handleState({ sendFeedbackStatus: LOADING });
    try {
      const response = await sendFeedback(data);
      if (response.data?.type === "warning") {
        sendAlert(
          response.data?.data?.message,
          "warning",
          true,
          null,
          WarningIcon
        );
        handleState({ sendFeedbackStatus: ERROR });
        return;
      }
      handleState({
        sendFeedbackStatus: SUCCESS,
      });
      sendAlert("Feedback has been sent successfully", "success");

      return response;
    } catch (error) {
      handleState({ sendFeedbackStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  }

  async function uploadImages(data) {
    handleState({ uploadImageStatus: LOADING });
    try {
      const response = await uploadImage(data);

      handleState({
        uploadImageStatus: SUCCESS,
      });
      return response;
    } catch (error) {
      handleState({ uploadImageStatus: ERROR });
      sendAlert(
        (error.response?.data?.errors?.file || [])[0] ||
          error.response?.data?.message,
        "error"
      );
      Promise.reject(error);
    }
  }

  async function uploadFiles(data) {
    handleState({ uploadFilesStatus: LOADING });
    try {
      const response = await uploadFile(data);

      handleState({
        uploadFilesStatus: SUCCESS,
      });
      return response;
    } catch (error) {
      handleState({ uploadFilesStatus: ERROR });
      sendAlert(
        (error.response?.data?.errors?.file || [])[0] ||
          error.response?.data?.message,
        "error"
      );
      Promise.reject(error);
    }
  }

  async function removeAttachments(data) {
    handleState({ deleteAttachmentStatus: LOADING });
    try {
      const response = await deleteAttachments(data);

      handleState({
        deleteAttachmentStatus: SUCCESS,
      });
      return response;
    } catch (error) {
      handleState({ deleteAttachmentStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  }

  async function fetchTreatmentTypes(type) {
    if (type === treatment) {
      handleState({ fetchTreatmentTypesStatus: LOADING });
    } else if (type === services) {
      handleState({ fetchServicesStatus: LOADING });
    } else if (type === detox) {
      handleState({ fetchDetoxListStatus: LOADING });
    } else if (type === payment_options) {
      handleState({ fetchPaymentOptionsStatus: LOADING });
    } else if (type === facility_types) {
      handleState({ fetchFacilityTypesStatus: LOADING });
    }
    try {
      const response = await getTratmentProgramConstants(type);
      if (type === treatment) {
        handleState({
          treatmentTypesList: response.data.data,
          fetchTreatmentTypesStatus: SUCCESS,
        });
      } else if (type === services) {
        handleState({
          servicesList: response.data.data,
          fetchServicesStatus: SUCCESS,
        });
      } else if (type === detox) {
        handleState({
          detoxList: response.data.data,
          fetchDetoxListStatus: SUCCESS,
        });
      } else if (type === payment_options) {
        handleState({
          paymentOptionsList: response.data.data,
          fetchPaymentOptionsStatus: SUCCESS,
        });
      } else if (type === facility_types) {
        handleState({
          facilityTypesList: response.data.data,
          fetchFacilityTypesStatus: SUCCESS,
        });
      }
    } catch (error) {
      if (type === treatment) {
        handleState({ fetchTreatmentTypesStatus: ERROR });
      } else if (type === services) {
        handleState({ fetchServicesStatus: ERROR });
      } else if (type === detox) {
        handleState({ fetchDetoxListStatus: ERROR });
      } else if (type === payment_options) {
        handleState({ fetchPaymentOptionsStatus: ERROR });
      } else if (type === facility_types) {
        handleState({ fetchFacilityTypesStatus: ERROR });
      }
      Promise.reject(error);
    }
  }

  const fetchCities = useCallback(async (data) => {
    handleState({ fetchCitiesStatus: LOADING });
    try {
      const response = await getCities(data);
      if (response && response.data) {
        handleState({
          citiesList: sortData(response.data),
          fetchCitiesStatus: SUCCESS,
        });
      }
    } catch (error) {
      handleState({ fetchCitiesStatus: ERROR });
    }
  }, []);

  const fetchZipcodes = useCallback(async (data) => {
    handleState({ fetchZipcodesStatus: LOADING });
    try {
      const response = await getZipcodesList(data);
      handleState({
        zipcodesList: response.data,
        fetchZipcodesStatus: SUCCESS,
      });
    } catch (error) {
      handleState({ fetchZipcodesStatus: ERROR });
      Promise.reject(error);
    }
  });

  const state = {
    ...common,
    fetchStatesList,
    fetchDegreesList,
    fetchLicensesList,
    fetchCertificatesList,
    fetchClientTypesList,
    getStateIdByAbbreviation,
    verifyEmailUser,
    unsubscribeEmailNotification,
    unsubscribeAllEmailNotification,
    getStatebyId,
    checkIfEmailExists,
    createFeedback,
    uploadImages,
    uploadFiles,
    removeAttachments,
    fetchTreatmentTypes,
    fetchCities,
    clearCitiesList,
    clearZipcodesList,
    handleState,
    fetchZipcodes,
    fetchReleaseVersion,
  };

  return (
    <CommonContext.Provider value={state}>{children}</CommonContext.Provider>
  );
};

export const useCommon = () => useContext(CommonContext);
